import React, { useCallback, useEffect, useRef } from "react";

/**
 * Editable table whose cells use a contentEditable <div> instead of a textarea.
 * This enables native browser bold / italic / underline / list rendering via
 * document.execCommand — called from the parent's mini-toolbar.
 *
 * Cell values are raw HTML strings stored in picoRows / hierarchyRows.
 * The parent serialises them to the protocol markdown via cellHtmlToMd().
 *
 * Cursor-jump prevention strategy
 * ─────────────────────────────────
 * We never let React touch innerHTML while the user is actively editing.
 * The useEffect only writes to the DOM when the stored HTML truly changed
 * from outside (AI generation, import, reorder) — not from the user's own
 * keystrokes, because handleInput reads el.innerHTML and immediately stores
 * the same string, so the equality check in the effect short-circuits.
 */
const ProtocolTableEditor = ({
    headers,
    rows,
    onChange,
    lockedColumns = [],
    showNumberColumn = false,
    canAddRows = false,
    canDeleteRows = false,
    minRows = 1,
    defaultNewRow,
    colWidths,          // optional string[] — explicit widths for each data column e.g. ["20%","40%","40%"]
}) => {
    // Map of "rowIdx-colIdx" → DOM div element
    const cellRefs = useRef({});

    // Sync external HTML changes (AI, import, reorder) into the DOM.
    // Skipped when incoming === el.innerHTML (i.e. the user just typed it).
    useEffect(() => {
        rows.forEach((row, rowIdx) => {
            row.forEach((val, colIdx) => {
                const el = cellRefs.current[`${rowIdx}-${colIdx}`];
                if (!el) return;
                const incoming = val || "";
                if (el.innerHTML !== incoming) {
                    el.innerHTML = incoming;
                }
            });
        });
    }, [rows]);

    // Read innerHTML on every input event and propagate to parent state
    const handleInput = useCallback(
        (rowIdx, colIdx, el) => {
            const next = rows.map((r, ri) => {
                if (ri !== rowIdx) return r;
                const nr = [...r];
                nr[colIdx] = el.innerHTML;
                return nr;
            });
            onChange(next);
        },
        [rows, onChange],
    );

    // Paste as plain text to avoid injecting external HTML/styles
    const handlePaste = useCallback((e) => {
        e.preventDefault();
        const text = e.clipboardData.getData("text/plain");
        if (text) document.execCommand("insertText", false, text);
    }, []);

    const handleAddRow = useCallback(() => {
        const newRow = defaultNewRow
            ? [...defaultNewRow]
            : new Array(headers.length).fill("");
        onChange([...rows, newRow]);
    }, [rows, onChange, defaultNewRow, headers.length]);

    const handleDeleteRow = useCallback(
        (rowIdx) => {
            if (rows.length <= minRows) return;
            onChange(rows.filter((_, i) => i !== rowIdx));
        },
        [rows, onChange, minRows],
    );

    return (
        <div className="pte-wrapper">
            <div className="table-responsive">
                <table className="table table-bordered pte-table mb-0">
                    {colWidths && (
                        <colgroup>
                            {showNumberColumn && <col style={{ width: "var(--px-40)" }} />}
                            {colWidths.map((w, i) => <col key={i} style={{ width: w }} />)}
                            {canDeleteRows && <col style={{ width: "var(--px-40)" }} />}
                        </colgroup>
                    )}
                    <thead>
                        <tr>
                            {showNumberColumn && (
                                <th className="pte-th pte-th-num ev-bold-med ev-small-p-dark">#</th>
                            )}
                            {headers.map((h, i) => (
                                <th key={i} className="pte-th ev-bold-med ev-small-p-dark">
                                    {h}
                                </th>
                            ))}
                            {canDeleteRows && <th className="pte-th pte-th-action" />}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((row, rowIdx) => (
                            <tr key={rowIdx}>
                                {showNumberColumn && (
                                    <td className="pte-td pte-td-num ev-bold-med ev-small-p-dark text-center align-middle">
                                        {rowIdx + 1}
                                    </td>
                                )}
                                {row.map((cellVal, colIdx) => {
                                    const isLocked = lockedColumns.includes(colIdx);
                                    return (
                                        <td
                                            key={colIdx}
                                            className={`pte-td${isLocked ? " pte-td-locked" : ""}`}
                                        >
                                            <div
                                                className={`pte-cell ev-small-p-dark${isLocked ? " ev-bold-med ev-reg-p-dark-blue" : ""}`}
                                                contentEditable={!isLocked}
                                                suppressContentEditableWarning
                                                ref={(el) => {
                                                    if (el) {
                                                        cellRefs.current[`${rowIdx}-${colIdx}`] = el;
                                                        // Set content only on first mount
                                                        if (!el.dataset.pteMounted) {
                                                            el.innerHTML = cellVal || "";
                                                            el.dataset.pteMounted = "1";
                                                        }
                                                    } else {
                                                        delete cellRefs.current[`${rowIdx}-${colIdx}`];
                                                    }
                                                }}
                                                onInput={(e) => {
                                                    if (isLocked) return;
                                                    handleInput(rowIdx, colIdx, e.currentTarget);
                                                }}
                                                onPaste={isLocked ? undefined : handlePaste}
                                            />
                                        </td>
                                    );
                                })}
                                {canDeleteRows && (
                                    <td className="pte-td pte-td-action align-middle">
                                        <button
                                            type="button"
                                            className="btn ev-btn-red-fill-round ev-btn-med ev-radius-xsmall pe-2 ps-2"
                                            onClick={() => handleDeleteRow(rowIdx)}
                                            disabled={rows.length <= minRows}
                                            title="Delete row"
                                        >
                                            <i className="bi bi-trash3" />
                                        </button>
                                    </td>
                                )}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            {canAddRows && (
                <button
                    type="button"
                    className="ev-btn-blue-outline-round ev-btn-sm mt-2"
                    onClick={handleAddRow}
                >
                    <i className="bi bi-plus me-1" />
                    Add Row
                </button>
            )}
        </div>
    );
};

export default ProtocolTableEditor;